import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { EstablishmentService } from '../../../services/establishment/establishment.service';
import { ToastService } from '../../../services/toast/toast.service';
import { LoaderComponent } from '../../../atoms/loader/loader.component';

// month-calendar of one room's occupancy for one utsav: each day shows how many
// beds are blocked (occupied/capacity). data = { roomId, roomNumber, utsavId }.
@Component({
  selector: 'app-room-occupancy-dialog',
  standalone: true,
  imports: [CommonModule, LoaderComponent],
  templateUrl: './room-occupancy-dialog.component.html',
  styleUrl: './room-occupancy-dialog.component.scss',
})
export class RoomOccupancyDialogComponent implements OnInit {
  readonly weekDays = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

  capacity: number = 0;
  utsavName: string = '';
  accommodationStartDate: string | null = null;
  accommodationEndDate: string | null = null;
  hasLoaded: boolean = false;
  hasError: boolean = false;
  isLoading: boolean = false;

  viewYear: number = 0;
  viewMonth: number = 0; // 0-11
  private occupiedByDate = new Map<string, number>();

  constructor(
    private establishmentService: EstablishmentService,
    private toastService: ToastService,
    private dialogRef: MatDialogRef<RoomOccupancyDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { roomId: number; roomNumber: string; utsavId: number }
  ) {}

  ngOnInit(): void {
    this.isLoading = true;
    this.establishmentService.getRoomOccupancy(this.data.roomId, this.data.utsavId).subscribe({
      next: (res: any) => {
        const d = res?.data;
        // some not-found responses come back 2xx with no payload — treat as errors.
        if (!d || d.days === undefined) {
          this.hasError = true;
          this.hasLoaded = true;
          this.isLoading = false;
          this.toastService.error(res?.message || 'Failed to load room occupancy.');
          return;
        }
        this.capacity = Number(d.capacity) || 0;
        this.utsavName = d.utsavName || '';
        this.accommodationStartDate = d.accommodationStartDate || null;
        this.accommodationEndDate = d.accommodationEndDate || null;
        this.occupiedByDate = new Map(
          (d.days || []).map((r: any) => [String(r.date), Number(r.occupiedBeds)])
        );
        this.setInitialMonth();
        this.hasLoaded = true;
        this.isLoading = false;
      },
      error: (err) => {
        this.hasError = true;
        this.hasLoaded = true;
        this.isLoading = false;
        this.toastService.error(err?.error?.message || 'Failed to load room occupancy.');
      },
    });
  }

  // start on the first month that matters: the first blocked date, else the
  // utsav accommodation start, else the current month.
  private setInitialMonth(): void {
    const firstBlocked = [...this.occupiedByDate.keys()].sort()[0];
    const anchor = firstBlocked || this.accommodationStartDate || this.toDateKey(new Date());
    this.viewYear = Number(anchor.slice(0, 4));
    this.viewMonth = Number(anchor.slice(5, 7)) - 1;
  }

  get monthLabel(): string {
    return new Date(this.viewYear, this.viewMonth, 1).toLocaleDateString('en-IN', {
      month: 'long',
      year: 'numeric',
    });
  }

  // grid cells for the month: leading blanks (null) then one entry per day.
  get monthCells(): Array<{ day: number; date: string; occupied: number; inWindow: boolean } | null> {
    const firstDay = new Date(this.viewYear, this.viewMonth, 1).getDay();
    const daysInMonth = new Date(this.viewYear, this.viewMonth + 1, 0).getDate();
    const cells: Array<any> = Array(firstDay).fill(null);
    for (let day = 1; day <= daysInMonth; day++) {
      const date = this.toKey(this.viewYear, this.viewMonth, day);
      cells.push({
        day,
        date,
        occupied: this.occupiedByDate.get(date) ?? 0,
        inWindow:
          (!this.accommodationStartDate || date >= this.accommodationStartDate) &&
          (!this.accommodationEndDate || date <= this.accommodationEndDate),
      });
    }
    return cells;
  }

  get hasNoBlockedDates(): boolean {
    return this.occupiedByDate.size === 0;
  }

  previousMonth(): void {
    this.viewMonth -= 1;
    if (this.viewMonth < 0) {
      this.viewMonth = 11;
      this.viewYear -= 1;
    }
  }

  nextMonth(): void {
    this.viewMonth += 1;
    if (this.viewMonth > 11) {
      this.viewMonth = 0;
      this.viewYear += 1;
    }
  }

  private toKey(year: number, month: number, day: number): string {
    return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  }

  private toDateKey(d: Date): string {
    return this.toKey(d.getFullYear(), d.getMonth(), d.getDate());
  }

  close(): void {
    this.dialogRef.close();
  }
}
